import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  NotesOutlined as NotesIcon,
  Today as TodayIcon,
  Title as TitleIcon,
  Subtitles as SubtitlesIcon,
  Functions as FunctionsIcon,
  AddCircleOutline as ExtraFieldIcon,
} from "@mui/icons-material";
import { startCase } from "lodash";

export const GENERIC_FORM_FIELDS = [
  {
    field_id: "generic_text",
    field_label: "Text",
    field_type: "text",
    type: "PDFTextField",
    description: "Reusable generic single-line text input",
    icon: <TextFieldsIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_number",
    field_label: "Number",
    field_type: "number",
    type: "PDFTextField",
    description: "Reusable generic numeric input",
    icon: <NumbersIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_dollar",
    field_label: "Dollar",
    field_type: "dollar",
    type: "PDFTextField",
    description: "Reusable currency and dollar amount field",
    icon: <AttachMoneyIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_date",
    field_label: "Date",
    field_type: "date",
    type: "PDFTextField",
    description: "Reusable generic date field",
    icon: <CalendarTodayIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_multiline",
    field_label: "Multiline",
    field_type: "multiline",
    type: "PDFTextField",
    description: "Reusable multi-line text area",
    icon: <NotesIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_email",
    field_label: "Email",
    field_type: "email",
    type: "PDFTextField",
    description: "Reusable generic email field",
    icon: <EmailIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_phone",
    field_label: "Phone",
    field_type: "mobile",
    type: "PDFTextField",
    description: "Reusable generic phone / mobile number",
    icon: <PhoneIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_money",
    field_label: "Money",
    field_type: "money",
    type: "PDFTextField",
    description: "Reusable generic monetary value field",
    icon: <AttachMoneyIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_todaydate",
    field_label: "Today Date",
    field_type: "todayDate",
    type: "PDFTextField",
    description: "Automatically uses the current date",
    icon: <TodayIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_full_title",
    field_label: "Full Title",
    field_type: "full_title",
    type: "PDFTextField",
    description: "Reusable field for a full title or heading",
    icon: <TitleIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_full_subtitle",
    field_label: "Full Sub Title",
    field_type: "full_subTitle",
    type: "PDFTextField",
    description: "Reusable field for a full subtitle or subheading",
    icon: <SubtitlesIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_total",
    field_label: "Total",
    field_type: "total",
    type: "PDFTextField",
    description: "Reusable field for displaying a calculated total",
    icon: <FunctionsIcon fontSize="small" color="primary" />,
  },
];

const GenericFormFieldPanel = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  onNotify,
  loading: loadingDoc = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFields = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return GENERIC_FORM_FIELDS;
    return GENERIC_FORM_FIELDS.filter(
      (f) =>
        f.field_label.toLowerCase().includes(q) ||
        f.field_type.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q),
    );
  }, [searchQuery]);

  const handleAttach = (e, field) => {
    e.stopPropagation();

    const tempFields = currentProcessingFile?.document?.tempFields;

    if (!tempFields?.selectedExtraFields?.length) {
      onNotify?.(
        "error",
        "Select a field from the PDF first, then you'll be able to assign it.",
      );
      return;
    }

    if (tempFields?.selectedExtraFields?.length > 1) {
      onNotify?.(
        "error",
        "You have selected more than one field. Only a single field can be assigned at a time.",
      );
      return;
    }

    const selectedField = tempFields?.selectedExtraFields?.[0] ?? {};

    const updatedField = {
      ...selectedField,

      field_label: selectedField?.field_label,
      label: selectedField?.label || selectedField?.field_label,
      field_id: field.field_id,
      field_type: field.field_type,
      is_generic: true,
      location: "create",
      ...(field.type && { type: field.type }),
      ...(field.field_type === "percentageCheckbox" && { hidden: false }),
    };

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (selectedField?.name === f?.name) {
        return updatedField;
      }
      return f;
    });

    const updatedSelectedFields = [updatedField];

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: updatedFormFields,
          selectedFormField: updatedSelectedFields[0],
          selectedExtraFields: updatedSelectedFields,
        },
      },
    }));

    onNotify?.("success", `Field tagged as generic ${field.field_label}`);
  };

  const createExtraFieldName = () => {
    if (typeof crypto !== "undefined" && crypto?.randomUUID) {
      return `extra_field_${crypto.randomUUID()}`;
    }

    return `extra_field_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const handleAddAsExtraField = (e, field) => {
    e.stopPropagation();

    if (!field?.field_id) {
      onNotify?.("error", "Cannot add field: missing field id.");
      return;
    }

    const tempFields = currentProcessingFile?.document?.tempFields;
    if (!tempFields) return;

    // Block: field is already tagged/assigned anywhere in formFields
    const alreadyTagged = tempFields.formFields?.some(
      (f) => f?.field_id === field.field_id,
    );

    if (alreadyTagged) {
      onNotify?.(
        "error",
        `"${field.field_label ?? field.field_name}" is already tagged as extra form field.`,
      );
      return;
    }

    const newExtraField = {
      name: createExtraFieldName(),
      field_label: field.field_label ?? field.field_name,
      field_id: field.field_id,
      field_type: field.field_type,
      location: field.location || "create",
      hidden: true, // makes it show up in Extra Form Fields drawer
      block_id: "",
      block_label: "",
    };

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: [
            ...(prevFile.document.tempFields.formFields || []),
            newExtraField,
          ],
        },
      },
    }));

    onNotify?.(
      "success",
      `"${field.field_label ?? field.field_name}" added as extra form field.`,
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        height: "calc(100% - 98px)",
        overflow: "hidden",
      }}
    >
      {/* <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid #F3F4F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
            Generic Form Fields
          </Typography>
        </Box>
      </Box> */}

      <Box sx={{ px: 1.5, pb: 1.25, mt: 1.25 }}>
        <TextField
          id="genericSearchInput"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          placeholder="Search generic fields..."
          fullWidth
          sx={{
            "& .MuiInputBase-input": { fontSize: 14 },
          }}
          InputProps={{
            sx: { padding: "0px 10px" },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0, // Prevents flexbox overflow cut-off issues
          overflowY: "auto",
          px: 1.5,
        }}
      >
        <Stack gap={0.75}>
          {filteredFields.map((field) => (
            <Box
              key={field.field_id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                borderLeft: "3px solid #2e7d32",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#1F2937",
                    fontSize: "0.85rem",
                  }}
                >
                  {field.field_label}
                </Typography>

                {/* Chips Row */}
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={0.5}
                  sx={{ mt: 0.5 }}
                >
                  <Chip
                    label="Generic"
                    size="small"
                    variant="filled"
                    color="success"
                    sx={{
                      fontSize: "0.7rem",
                      height: 20,
                      borderRadius: 1,
                    }}
                  />
                  <Chip
                    label={startCase(field.field_type)}
                    size="small"
                    variant="filled"
                    sx={{
                      fontSize: "0.7rem",
                      height: 20,
                      borderRadius: 1,
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Tooltip title="Add as extra field" arrow placement="bottom">
                  <IconButton
                    size="small"
                    onClick={(e) => handleAddAsExtraField(e, field)}
                    sx={{
                      border: "1px solid #d0d7de",
                      borderRadius: 1.5,
                      backgroundColor: "transparent",
                      p: "4.41px !important",
                    }}
                    disabled={loadingDoc}
                  >
                    <ExtraFieldIcon
                      sx={{
                        fontSize: "1rem",
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(e) => handleAttach(e, field)}
                  sx={{
                    border: "1px solid #d0d7de",
                    borderRadius: 1.5,
                    backgroundColor: "transparent",
                    p: "4.41px !important",
                  }}
                  disabled={loadingDoc}
                >
                  <AttachFileIcon
                    sx={{
                      fontSize: "0.95rem",
                      transform: "rotate(45deg)",
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          ))}

          {filteredFields.length === 0 && (
            <Typography
              variant="body2"
              align="center"
              sx={{ color: "text.secondary", mt: 4 }}
            >
              No generic fields matching "{searchQuery}"
            </Typography>
          )}
        </Stack>
      </Box>
      <Box sx={{ height: 40, flexShrink: 0 }} />
    </Box>
  );
};

export default GenericFormFieldPanel;
